import { useState } from "react";
import { motion } from "framer-motion";
import { useMatch } from "@/match/MatchContext";
import { CommentaryButton } from "./StorySection";
import { SECTIONS } from "@/data/content";

// Renders an image inside a browser window; if the file is missing or
// fails to load, the window's accent colour panel shows with no text.
function WindowMedia({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={alt || ""}
        className="w-full h-full object-cover"
        onError={() => setFailed(true)}
      />
    );
  }
  return null;
}

// THE SEARCH — electric blue, technology-inspired, floating project windows.
export default function SearchSection() {
  const { openCommentary } = useMatch();
  const section = SECTIONS.search;

  return (
    <div
      className="relative bg-[#0052FF] border-b-4 border-black py-20 sm:py-28 px-4 sm:px-8 lg:px-16 overflow-hidden"
      data-testid="search-section"
    >
      {/* blueprint grid */}
      <div
        className="absolute inset-0 opacity-[0.12] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #FFFFFF 1px, transparent 1px), linear-gradient(to bottom, #FFFFFF 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
        aria-hidden="true"
      />
      <div className="relative max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.65 }}
        >
          <span
            className="inline-block bg-[#0A0A0A] text-[#84E116] font-broadcast font-bold text-[10px] sm:text-xs tracking-[0.28em] uppercase px-4 py-2 mb-6"
            data-testid="search-minute"
          >
            {section.minuteEvent}
          </span>
          <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.95] tracking-tight text-white mb-6">
            {section.headline}
          </h2>
          <p className="text-base sm:text-lg font-medium leading-relaxed text-white/85 max-w-lg mb-8">
            {section.preview}
          </p>
          <div className="[&>button]:bg-white">
            <CommentaryButton
              onClick={() => openCommentary("search")}
              testId="search-commentary-btn"
            />
          </div>
        </motion.div>

        {/* browser-style project windows */}
        <div className="relative h-[340px] sm:h-[420px]">
          <motion.div
            className="absolute left-0 top-0 w-[62%] border-[3px] border-black hard-shadow-lg bg-[#84E116] overflow-hidden"
            initial={{ opacity: 0, y: 40, rotate: -2 }}
            whileInView={{ opacity: 1, y: 0, rotate: -2 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.65, delay: 0.1 }}
          >
            <WindowBar />
            <div className="aspect-[16/10] flex items-center justify-center overflow-hidden">
              <WindowMedia
                src={section.mediaUrlA}
                alt={section.mediaAltA}
              />
            </div>
          </motion.div>
          <motion.div
            className="absolute right-0 bottom-0 w-[55%] border-[3px] border-black hard-shadow-lg bg-[#FFC7DA] overflow-hidden"
            initial={{ opacity: 0, y: 40, rotate: 2 }}
            whileInView={{ opacity: 1, y: 0, rotate: 2 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.65, delay: 0.22 }}
          >
            <WindowBar />
            <div className="aspect-[16/10] flex items-center justify-center overflow-hidden">
              <WindowMedia
                src={section.mediaUrlB}
                alt={section.mediaAltB}
                fallbackLabel={section.mediaLabel}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export function WindowBar() {
  return (
    <div className="flex items-center gap-1.5 bg-white border-b-[3px] border-black px-3 py-2">
      <span className="w-2.5 h-2.5 rounded-full bg-[#FF3B30] border border-black" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#FFDF00] border border-black" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#84E116] border border-black" />
    </div>
  );
}
